import { useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
    ArrowLeft,
    ShoppingCart,
    Plus,
    Minus,
    Leaf,
    CheckCircle2,
    MessageCircle,
} from 'lucide-react'
import { findProductById, getAllProducts } from '../utils/products'
import { useCart } from '../context/CartContext'
import { buildWhatsAppCheckoutUrl } from '../utils/whatsapp'

const ProductDetail = () => {
    const { productId } = useParams()
    const navigate = useNavigate()
    const { addToCart, openCart } = useCart()
    const [quantity, setQuantity] = useState(1)
    const [added, setAdded] = useState(false)

    const product = useMemo(() => findProductById(productId), [productId])

    const relatedProducts = useMemo(() => {
        if (!product) return []
        return getAllProducts()
            .filter((p) => p.categoryId === product.categoryId && p.id !== product.id)
            .slice(0, 3)
    }, [product])

    if (!product) {
        return (
            <div className="min-h-screen bg-neutral-light flex items-center justify-center px-4">
                <div className="text-center max-w-md">
                    <h1 className="text-3xl font-bold text-primary-green mb-4">
                        Produit introuvable
                    </h1>
                    <p className="text-gray-600 mb-8">
                        Le produit que vous recherchez n'existe pas ou a été retiré.
                    </p>
                    <Link to="/products" className="btn-primary">
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Retour aux produits
                    </Link>
                </div>
            </div>
        )
    }

    const handleAddToCart = () => {
        addToCart(product, quantity)
        setAdded(true)
        setTimeout(() => setAdded(false), 1500)
    }

    const handleBuyNow = () => {
        addToCart(product, quantity)
        const url = buildWhatsAppCheckoutUrl([
            {
                id: product.id,
                name: product.name,
                categoryName: product.categoryName,
                quantity,
            },
        ])
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    return (
        <div className="min-h-screen bg-neutral-light">
            {/* Breadcrumb */}
            <section className="bg-white border-b border-gray-200">
                <div className="container-custom py-4">
                    <nav className="flex items-center text-sm flex-wrap gap-2">
                        <Link to="/" className="text-gray-500 hover:text-primary-green transition-colors">
                            Accueil
                        </Link>
                        <span className="text-gray-300">/</span>
                        <Link to="/products" className="text-gray-500 hover:text-primary-green transition-colors">
                            Produits
                        </Link>
                        <span className="text-gray-300">/</span>
                        <span className="text-primary-green font-medium">{product.categoryName}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-primary-green font-semibold">{product.name}</span>
                    </nav>
                </div>
            </section>

            {/* Product Section */}
            <section className="section-padding">
                <div className="container-custom">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-primary-green font-semibold mb-8 hover:text-primary-light transition-colors group"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                        Retour
                    </button>

                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Image */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="rounded-3xl overflow-hidden shadow-2xl bg-white">
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-[500px] object-cover"
                                />
                            </div>
                            {product.bio && (
                                <div className="absolute top-6 left-6 bg-primary-yellow text-primary-green px-4 py-2 rounded-full font-semibold flex items-center shadow-lg">
                                    <Leaf className="w-4 h-4 mr-2" />
                                    100% Bio
                                </div>
                            )}
                        </motion.div>

                        {/* Content */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="inline-block px-4 py-2 bg-primary-yellow/20 text-primary-green rounded-full text-sm font-semibold mb-4">
                                {product.categoryName}
                            </span>
                            <h1 className="text-4xl lg:text-5xl font-bold text-primary-green mb-4">
                                {product.name}
                            </h1>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                {product.description}
                            </p>

                            {/* Varieties */}
                            <div className="mb-8">
                                <h3 className="font-semibold text-primary-green mb-3">
                                    Variétés disponibles
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {product.items.map((item, idx) => (
                                        <span
                                            key={idx}
                                            className="px-4 py-2 bg-primary-green/10 text-primary-green rounded-full font-medium text-sm"
                                        >
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Benefits */}
                            <div className="mb-8 bg-white rounded-2xl p-6 shadow-md">
                                <h3 className="font-semibold text-primary-green mb-4">Nos garanties</h3>
                                <ul className="space-y-3">
                                    {(product.bio
                                        ? [
                                            '100% biologique et naturel',
                                            'Produit localement au Bénin',
                                            'Sans OGM ni pesticides chimiques',
                                            'Fraîcheur et qualité garanties',
                                        ]
                                        : [
                                            'Matériel neuf, garanti à la livraison',
                                            'Prise en main et conseils d\'utilisation',
                                            'Pièces et accessoires disponibles',
                                            'Tarifs dégressifs sur les commandes en gros',
                                        ]
                                    ).map((benefit, idx) => (
                                        <li key={idx} className="flex items-start text-gray-700">
                                            <CheckCircle2 className="w-5 h-5 text-primary-green mr-3 mt-0.5 flex-shrink-0" />
                                            <span>{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Quantity Selector */}
                            <div className="mb-6">
                                <label className="block font-semibold text-primary-green mb-3">
                                    Quantité
                                </label>
                                <div className="inline-flex items-center bg-white border-2 border-primary-green/20 rounded-full">
                                    <button
                                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                        className="w-12 h-12 rounded-full flex items-center justify-center hover:bg-primary-green hover:text-white transition-colors text-primary-green"
                                        aria-label="Diminuer la quantité"
                                    >
                                        <Minus className="w-5 h-5" />
                                    </button>
                                    <span className="w-12 text-center text-lg font-bold text-primary-green">
                                        {quantity}
                                    </span>
                                    <button
                                        onClick={() => setQuantity((q) => q + 1)}
                                        className="w-12 h-12 rounded-full flex items-center justify-center hover:bg-primary-green hover:text-white transition-colors text-primary-green"
                                        aria-label="Augmenter la quantité"
                                    >
                                        <Plus className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={handleAddToCart}
                                    className={`btn-primary group flex-1 transition-all ${added ? '!bg-green-600' : ''
                                        }`}
                                >
                                    <ShoppingCart className="w-5 h-5 mr-2" />
                                    {added ? 'Ajouté !' : 'Ajouter au panier'}
                                </button>
                                <button
                                    onClick={handleBuyNow}
                                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    <MessageCircle className="w-5 h-5 mr-2" />
                                    Commander maintenant
                                </button>
                            </div>

                            <button
                                onClick={openCart}
                                className="mt-4 text-sm text-primary-green hover:text-primary-light font-semibold transition-colors"
                            >
                                Voir mon panier →
                            </button>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Related Products */}
            {relatedProducts.length > 0 && (
                <section className="section-padding bg-white">
                    <div className="container-custom">
                        <h2 className="text-3xl font-bold text-primary-green mb-8 text-center">
                            Vous pourriez aussi aimer
                        </h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            {relatedProducts.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/products/${p.id}`}
                                    className="group bg-neutral-light rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                                >
                                    <div className="h-56 overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={p.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-primary-green mb-2">
                                            {p.name}
                                        </h3>
                                        <p className="text-gray-600 text-sm">{p.description}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    )
}

export default ProductDetail
